import { FormEvent, useState } from 'react';
import { dealerErrorCode, dealerErrorDetail, dealerErrorMessage, dealersApi } from '../../services/dealersApi';
import { CreditRequest, MarkPaidResult } from '../../types/dealers';
import { describedBy, FormField } from '../promotions/PromoFormParts';
import { aed, amountError, humanize, sameAmount, todayDubai } from './dealerFormat';

interface MarkPaidDialogProps {
  dealerId: string;
  request: CreditRequest;
  token: string;
  onClose: () => void;
  onPaid: (result: MarkPaidResult) => void;
  /** 409 ALREADY_PAID / INVALID_STATE: someone else got there first; the caller reloads. */
  onStale: (message: string) => void;
}

/**
 * Spec 085 Flow E — record the dealer's bank transfer. The received amount must equal payable + VAT
 * (BR-14): a different figure warns here and the server refuses it with 422 `AMOUNT_MISMATCH`, shown
 * on the field. Open while the switch is off (the money already arrived).
 */
export default function MarkPaidDialog({ dealerId, request, token, onClose, onPaid, onStale }: MarkPaidDialogProps) {
  const [bankReference, setBankReference] = useState('');
  const [received, setReceived] = useState('');
  const [valueDate, setValueDate] = useState(todayDubai());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const expected = request.totalDueAed;
  const mismatch = received.trim() !== '' && !amountError(received) && !sameAmount(received, expected);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    const local: Record<string, string> = {};
    if (!bankReference.trim()) local.bankReference = 'Enter the bank reference';
    const amt = amountError(received);
    if (amt) local.receivedAmountAed = amt;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(valueDate)) local.valueDate = 'Pick the value date';
    if (Object.keys(local).length) { setErrors(local); return; }
    setErrors({});
    setSaving(true);
    try {
      onPaid(await dealersApi.markPaid(dealerId, request.id, {
        bankReference: bankReference.trim(), receivedAmountAed: received.trim(), valueDate,
      }, token));
    } catch (err) {
      const code = dealerErrorCode(err);
      if (code === 'AMOUNT_MISMATCH') {
        const exp = dealerErrorDetail(err, 'expectedAed') ?? expected;
        setErrors({ receivedAmountAed: `Nothing was saved. The received amount must equal the amount due, ${aed(exp)}.` });
      } else if (code === 'ALREADY_PAID' || code === 'INVALID_STATE') {
        onStale(dealerErrorMessage(err));
      } else {
        setFormError(dealerErrorMessage(err, 'Failed to mark the request paid'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Mark ${request.number ?? 'request'} paid`} style={{ maxWidth: '520px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>Mark {request.number ?? 'request'} paid</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {formError && <div className="admin-info-banner danger" role="alert" data-testid="mark-paid-error">{formError}</div>}
            <dl className="dealer-summary" data-testid="mark-paid-summary">
              <div><dt>Type</dt><dd>{request.kind === 'PACK' ? 'Credit pack' : 'Per-buyer credit'}</dd></div>
              <div><dt>Face value</dt><dd>{aed(request.faceValueAed)}</dd></div>
              <div><dt>Payable</dt><dd>{aed(request.payableAed)}</dd></div>
              <div><dt>VAT</dt><dd>{aed(request.vatAed)}</dd></div>
              <div><dt>Amount due</dt><dd><strong data-testid="mark-paid-expected">{aed(expected)}</strong></dd></div>
              <div><dt>Status</dt><dd>{humanize(request.status)}</dd></div>
            </dl>
            <FormField id="mark-paid-reference" label="Bank reference" error={errors.bankReference}>
              <input id="mark-paid-reference" className="admin-form-input" value={bankReference} maxLength={100} disabled={saving}
                {...describedBy('mark-paid-reference', errors.bankReference)} onChange={(e) => setBankReference(e.target.value)} />
            </FormField>
            <FormField id="mark-paid-received" label="Received amount (AED)" error={errors.receivedAmountAed}>
              <input id="mark-paid-received" className="admin-form-input" inputMode="decimal" value={received} disabled={saving}
                {...describedBy('mark-paid-received', errors.receivedAmountAed)} onChange={(e) => { setReceived(e.target.value); setErrors((p) => ({ ...p, receivedAmountAed: '' })); }} />
            </FormField>
            {mismatch && !errors.receivedAmountAed && (
              <div className="admin-info-banner warning" role="alert" data-testid="mark-paid-mismatch">
                This is not the amount due ({aed(expected)}). The payment can only be recorded for the exact amount; check the transfer with the dealer.
              </div>
            )}
            <FormField id="mark-paid-date" label="Value date" error={errors.valueDate}>
              <input id="mark-paid-date" className="admin-form-input" type="date" value={valueDate} disabled={saving} style={{ maxWidth: '12rem' }}
                {...describedBy('mark-paid-date', errors.valueDate)} onChange={(e) => setValueDate(e.target.value)} />
            </FormField>
            <div className="dealer-muted">
              Marking paid issues the dealer tax invoice and {request.kind === 'PACK' ? "adds the credit to the dealer's balance" : "makes the buyer's credit live"}.
            </div>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Mark paid'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
